import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, Image as ImageIcon, Copy, Download, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['auto', 'Solar', 'Battery', 'Rebates', 'Hot Water', 'Guides'];

export const BlogAiStudio = ({ token }) => {
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState('auto');
  const [draft, setDraft] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);

  const [imagePrompt, setImagePrompt] = useState('');
  const [imageData, setImageData] = useState(null); // { mime, base64 }
  const [loadingImage, setLoadingImage] = useState(false);

  const draftJson = draft ? JSON.stringify(draft, null, 2) : '';

  const generateDraft = async () => {
    if (!topic.trim()) {
      toast.error('Enter a topic or title first');
      return;
    }
    setLoadingDraft(true);
    setDraft(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/blog/generate-draft?token=${token}`,
        { topic, category: category === 'auto' ? null : category }
      );
      setDraft(res.data.draft);
      if (!imagePrompt) setImagePrompt(res.data.draft.title || topic);
      toast.success('Draft generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Draft generation failed');
    } finally {
      setLoadingDraft(false);
    }
  };

  const generateImage = async () => {
    const p = (imagePrompt || topic).trim();
    if (!p) {
      toast.error('Enter an image prompt (or generate a draft first)');
      return;
    }
    setLoadingImage(true);
    setImageData(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/blog/generate-image?token=${token}`,
        { prompt: p }
      );
      setImageData({ mime: res.data.mime_type, base64: res.data.image_base64 });
      toast.success('Image generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image generation failed');
    } finally {
      setLoadingImage(false);
    }
  };

  const copyJson = () => {
    navigator.clipboard.writeText(draftJson);
    toast.success('Article object copied — paste into src/data/blogPosts.js');
  };

  const downloadImage = () => {
    if (!imageData) return;
    const ext = imageData.mime.includes('png') ? 'png' : 'jpg';
    const a = document.createElement('a');
    a.href = `data:${imageData.mime};base64,${imageData.base64}`;
    a.download = `${draft?.slug || 'blog-hero'}.${ext}`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="blog-ai-studio">
      <div className="bg-gradient-to-r from-cyo-orange/10 to-orange-50 border border-orange-200 rounded-xl p-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-cyo-orange" /> Blog AI Studio
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Generate an SEO-ready article draft and a hero image. Copy the article object into{' '}
          <code className="bg-white px-1 rounded border">src/data/blogPosts.js</code>, save the image
          to <code className="bg-white px-1 rounded border">public/blog/</code> and set its path on{' '}
          <code className="bg-white px-1 rounded border">heroImage</code>, then redeploy.
        </p>
      </div>

      {/* Draft generator */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-bold text-gray-900 mb-3">1. Generate article draft</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="blog-ai-topic"
            placeholder="Topic or title, e.g. How EV chargers save money at home"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="flex-1"
          />
          <select
            data-testid="blog-ai-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="border border-gray-200 rounded-md px-3 py-2 text-sm"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c === 'auto' ? 'Auto category' : c}</option>
            ))}
          </select>
          <Button
            data-testid="blog-ai-generate-draft"
            onClick={generateDraft}
            disabled={loadingDraft}
            className="bg-cyo-orange hover:bg-orange-600 text-white"
          >
            {loadingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            <span className="ml-2">Generate</span>
          </Button>
        </div>

        {draft && (
          <div className="mt-5 space-y-3" data-testid="blog-ai-draft-result">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><span className="font-semibold text-gray-700">Title:</span> {draft.title}</div>
              <div><span className="font-semibold text-gray-700">Slug:</span> /blog/{draft.slug}</div>
              <div><span className="font-semibold text-gray-700">Category:</span> {draft.category}</div>
              <div><span className="font-semibold text-gray-700">Read:</span> {draft.readMinutes} min</div>
              <div className="sm:col-span-2"><span className="font-semibold text-gray-700">Meta:</span> {draft.metaDescription}</div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Article object (paste into blogPosts.js)</span>
              <Button size="sm" variant="outline" onClick={copyJson} data-testid="blog-ai-copy">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
            </div>
            <textarea
              data-testid="blog-ai-json"
              readOnly
              value={draftJson}
              className="w-full h-64 font-mono text-xs bg-gray-950 text-emerald-300 rounded-lg p-3 border border-gray-800"
            />
          </div>
        )}
      </div>

      {/* Image generator */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-bold text-gray-900 mb-3">2. Generate hero image (optional)</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="blog-ai-image-prompt"
            placeholder="Image prompt (defaults to your article title)"
            value={imagePrompt}
            onChange={(e) => setImagePrompt(e.target.value)}
            className="flex-1"
          />
          <Button
            data-testid="blog-ai-generate-image"
            onClick={generateImage}
            disabled={loadingImage}
            className="bg-gray-900 hover:bg-gray-800 text-white"
          >
            {loadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            <span className="ml-2">Generate image</span>
          </Button>
        </div>

        {imageData && (
          <div className="mt-5" data-testid="blog-ai-image-result">
            <img
              src={`data:${imageData.mime};base64,${imageData.base64}`}
              alt="Generated hero"
              className="w-full max-w-xl rounded-lg border border-gray-200"
            />
            <Button className="mt-3 bg-cyo-orange hover:bg-orange-600 text-white" onClick={downloadImage} data-testid="blog-ai-download-image">
              <Download className="w-4 h-4 mr-2" /> Download image ({draft?.slug || 'blog-hero'})
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              Save this into <code className="bg-gray-100 px-1 rounded">public/blog/</code> and set
              <code className="bg-gray-100 px-1 rounded mx-1">{`"heroImage": "/blog/${draft?.slug || 'your-slug'}.jpg"`}</code>
              on the article.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
